import { type FetchResult, gql, type MutationHookOptions, type TypedDocumentNode, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import type { UpdateOrgSettings, UpdateOrgSettingsVariables } from 'types/graphql';
import { ORGANIZATION_SETTINGS_QUERY } from './organizationSettingsQuery';

// Operation name `UpdateOrgSettings` is kept for telemetry although it calls `updateOrganization`.
const UPDATE_ORGANIZATION: TypedDocumentNode<UpdateOrgSettings, UpdateOrgSettingsVariables> = gql`
  mutation UpdateOrgSettings($input: UpdateOrganizationInput!) {
    updateOrganization(input: $input) {
      id
    }
  }
`;

type UpdateOrganizationOptions = Pick<
  MutationHookOptions<UpdateOrgSettings, UpdateOrgSettingsVariables>,
  'onCompleted' | 'onError'
>;

/** The caller's organization (`updateOrganization`, no id: R-O1); refetches the active settings read. */
export function useUpdateOrganization(options: UpdateOrganizationOptions = {}) {
  const [mutate, { loading, error }] = useMutation(UPDATE_ORGANIZATION, {
    refetchQueries: [ORGANIZATION_SETTINGS_QUERY],
    ...options,
  });
  const update = useCallback(
    ({ input }: UpdateOrgSettingsVariables): Promise<FetchResult<UpdateOrgSettings>> =>
      mutate({ variables: { input } }),
    [mutate]
  );

  return { update, loading, error };
}
